import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ChevronDown, Lock, Plus } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { useAdminMode } from "@/lib/adminState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const adminMode = useAdminMode();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/browse?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 border-b border-white/5 bg-black/60 backdrop-blur-2xl"
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 md:gap-8 md:px-12 md:py-4">
        <Logo size="sm" className="shrink-0" />

        <form
          onSubmit={onSearch}
          className="relative hidden flex-1 max-w-xl md:block"
          data-testid="nav-search-form"
        >
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#666]" />
          <input
            data-testid="nav-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the vault..."
            className="w-full rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-11 py-2.5 text-sm text-[#f2f2f2] placeholder:text-[#666] focus:border-[#e60039] focus:outline-none focus:ring-1 focus:ring-[#e60039]"
          />
        </form>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-categories-trigger"
                aria-label="Categories"
                className="inline-flex items-center gap-1.5 rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-2.5 py-2 text-[10px] uppercase tracking-[0.18em] text-[#f2f2f2] transition-colors hover:bg-[#1a1a1a] sm:px-3 sm:text-xs md:text-sm"
              >
                Categories
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="z-[60] w-56 border-[#1a1a1a] bg-[#0a0a0a] text-[#f2f2f2]"
            >
              {categories.map((c) => (
                <DropdownMenuItem
                  key={c.slug}
                  data-testid={`nav-cat-${c.slug}`}
                  onClick={() => navigate(`/category/${c.slug}`)}
                  className="cursor-pointer focus:bg-[#1a1a1a] focus:text-[#e60039]"
                >
                  {c.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                data-testid="nav-cat-all"
                onClick={() => navigate("/browse")}
                className="cursor-pointer border-t border-[#1a1a1a] focus:bg-[#1a1a1a] focus:text-[#e60039]"
              >
                Browse All
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => navigate(adminMode ? "/admin/dashboard" : "/admin")}
            data-testid={adminMode ? "nav-admin-add" : "nav-admin-link"}
            aria-label={adminMode ? "Add video" : "Admin"}
            className={`inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-2 text-[10px] uppercase tracking-[0.18em] transition-colors sm:px-3 sm:text-xs md:text-sm ${
              adminMode
                ? "border-[#e60039] bg-[#e60039] text-white hover:bg-[#b3002c]"
                : "border-[#1a1a1a] bg-[#0f0f0f] text-[#a3a3a3] hover:border-[#e60039] hover:text-[#e60039]"
            }`}
          >
            {adminMode ? <Plus className="h-4 w-4" /> : <Lock className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{adminMode ? "Add" : "Admin"}</span>
          </button>
        </div>
      </div>

      {/* mobile search */}
      <form onSubmit={onSearch} className="border-t border-white/5 px-4 py-2 md:hidden">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#666]" />
          <input
            data-testid="nav-search-input-mobile"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the vault..."
            className="w-full rounded-sm border border-[#1a1a1a] bg-[#0f0f0f] px-11 py-2 text-sm text-[#f2f2f2] placeholder:text-[#666] focus:border-[#e60039] focus:outline-none"
          />
        </div>
      </form>
    </header>
  );
};

export default Navbar;
